import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const SPEC_GUIDES_QUERY = `
    query GetGuidesBySpecGuid($specGuid: String!) {
        getGuidesBySpecGuid(specGuid: $specGuid) {
            id
            guideSpecId
            contextId
            contextProduct
            guideCmsId
            guideDocId
            guideType
            guideTitle
            guideDocStatus
            createdDate
            modifiedDate
        }
    }
`;

const OPERATION_GUIDES_QUERY = `
    query GetGuidesByOperationGuid($canonicalSpecGuid: String!, $operationGuid: String!) {
        getGuidesByOperationGuid(canonicalSpecGuid: $canonicalSpecGuid, operationGuid: $operationGuid) {
            id
            canonicalSpecGuid
            operationGuid
            guideCmsId
            guideDocId
            guideType
            guideTitle
            guideDocStatus
            createdDate
            modifiedDate
        }
    }
`;

const CIR_GUIDES_PROXY_PATH = `${import.meta.env.VITE_API_PATH}/services/cir-guides`;
const CIR_GRAPHQL_URL = import.meta.env.VITE_CIR_GRAPHQL_URL;

const cirGuidesApi = createApi({
    reducerPath: 'cirGuidesApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: (headers) => {
            headers.set('Content-Type', 'application/json');
            headers.set('X-Requested-With', 'XMLHttpRequest');
            headers.set('Accept', 'application/json');
            return headers;
        },
    }),
    endpoints: (builder) => ({
        fetchCirGuides: builder.query({
            /**
             * @param {{ specGuid: string, canonicalSpecGuid: string, operationGuids: string[] }} args
             * @returns {{ specGuides: object[], operationGuides: Record<string, object[]> }}
             */
            async queryFn({ specGuid, canonicalSpecGuid, operationGuids: operationGuidsRaw = [] }, _api, _extraOptions, fetchWithBQ) {
                const operationGuids = Array.isArray(operationGuidsRaw)
                    ? [...new Set(operationGuidsRaw.filter(operationGuid => typeof operationGuid === 'string' && operationGuid.trim()))]
                    : [];

                if (!specGuid) {
                    return { error: { status: 400, data: { message: 'specGuid is required' } } };
                }

                if (!CIR_GRAPHQL_URL) {
                    return fetchWithBQ({
                        url: CIR_GUIDES_PROXY_PATH,
                        method: 'POST',
                        body: { specGuid, canonicalSpecGuid, operationGuids },
                    });
                }

                const graphqlPost = async (query, variables) => {
                    const result = await fetchWithBQ({
                        url: CIR_GRAPHQL_URL,
                        method: 'POST',
                        body: { query, variables },
                    });

                    if (result.error) {
                        throw new Error(result.error?.data?.message || 'CIR GraphQL request failed');
                    }

                    return result.data;
                };

                const specPromise = graphqlPost(SPEC_GUIDES_QUERY, { specGuid })
                    .catch(() => null);

                const opPromises = (!canonicalSpecGuid || !operationGuids.length)
                    ? []
                    : operationGuids.map(operationGuid =>
                        graphqlPost(OPERATION_GUIDES_QUERY, { canonicalSpecGuid, operationGuid })
                            .then(data => ({ operationGuid, data }))
                            .catch(() => ({ operationGuid, data: null }))
                    );

                const [specResult, ...opResults] = await Promise.all([specPromise, ...opPromises]);
                const specGuides = specResult?.data?.getGuidesBySpecGuid || [];

                const operationGuides = {};
                for (const operationGuid of operationGuids) {
                    operationGuides[operationGuid] = [];
                }
                for (const { operationGuid, data } of opResults) {
                    operationGuides[operationGuid] = data?.data?.getGuidesByOperationGuid || [];
                }

                return { data: { specGuides, operationGuides } };
            },
        }),
    }),
});

export const { useFetchCirGuidesQuery } = cirGuidesApi;
export { cirGuidesApi };
